// Phase SA-5 — Brief §4.3: 4-stage processing animation.
//
// While the autopsy runs, the user must never see a blank screen
// or a loading spinner alone (brief §4.3 verbatim). The processing
// state is an active, intelligent-feeling experience.
//
// 4 stages with a faux time progression. We can't synchronize the
// dots to the actual Edge fn progress (no SSE for this endpoint),
// so we drive them on a local timer that loops once. SA-6 may add
// real progress signals; until then the timed walkthrough is the
// honest representation — "we're working on it" with structure,
// not "loading" with no detail.

import { useEffect, useState } from "react";

export type ProcessingStage =
  | "extraction"
  | "analysis"
  | "verdict"
  | "profile";

const STAGES: ReadonlyArray<{
  id: ProcessingStage;
  label: string;
  approxMs: number;
}> = [
  { id: "extraction", label: "Agent extracting legs from your slip…", approxMs: 2500 },
  { id: "analysis", label: "Analyzing each leg against STRIKZA's intelligence…", approxMs: 5500 },
  { id: "verdict", label: "Generating verdicts…", approxMs: 3000 },
  { id: "profile", label: "Updating your betting profile…", approxMs: 1500 },
];

export interface ProcessingStateProps {
  /** When true, advance stages on a timer. Goes idle when false. */
  active: boolean;
}

export function ProcessingState({
  active,
}: ProcessingStateProps): JSX.Element | null {
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    if (!active) {
      setStageIdx(0);
      return;
    }
    let cumulative = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < STAGES.length - 1; i++) {
      cumulative += STAGES[i]!.approxMs;
      const t = setTimeout(() => setStageIdx(i + 1), cumulative);
      timers.push(t);
    }
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div className="sa-processing" data-testid="sa-processing" role="status">
      <div className="sa-processing-headline">
        SIKA is auditing this slip
      </div>
      {STAGES.map((stage, i) => {
        const state: "queued" | "active" | "done" =
          i < stageIdx ? "done" : i === stageIdx ? "active" : "queued";
        return (
          <div
            key={stage.id}
            className="sa-processing-stage"
            data-stage={stage.id}
            data-state={state}
            data-testid={`sa-processing-${stage.id}`}
          >
            <span className="sa-processing-dot" aria-hidden />
            <span>{stage.label}</span>
          </div>
        );
      })}
    </div>
  );
}
